// Layer stack: one place that answers "what does Escape close?" and "what does the
// Android back button close?".
//
// Every open sheet/modal/lightbox pushes a layer with its close function. Escape closes
// only the top layer (and stops the event so page-level handlers do not close everything
// underneath at once). Opening a layer also pushes a history entry, so Back closes the
// layer instead of leaving the page; closing it any other way pops that entry again.

interface Layer {
    id: number;
    close: () => void;
}

const stack: Layer[] = [];
let seq = 0;
// Id of the layer being closed because history moved back: its close() must not call
// history.back() a second time.
let closingFromHistory: number | null = null;
// Set when we call history.back() ourselves so the resulting popstate is ignored.
let ignoreNextPop = false;

function stateLayerId(): number | undefined {
    const s = history.state as { docLayer?: number } | null;
    return s && typeof s.docLayer === 'number' ? s.docLayer : undefined;
}

export interface LayerOptions {
    /** Push a history entry so the back button closes the layer. Default true. */
    history?: boolean;
}

export function pushLayer(close: () => void, opts: LayerOptions = {}): number {
    const id = ++seq;
    stack.push({ id, close });
    if (opts.history !== false) {
        try { history.pushState({ docLayer: id }, ''); } catch { /* sandboxed or file: — fine */ }
    }
    return id;
}

export function popLayer(id: number): void {
    const i = stack.findIndex(l => l.id === id);
    if (i < 0) return;
    stack.splice(i, 1);
    if (closingFromHistory === id) return;
    // Closed by X / overlay / Escape while our entry is current: drop it so Back
    // afterwards still leaves the page as the user expects.
    if (stateLayerId() === id) {
        ignoreNextPop = true;
        try { history.back(); } catch { ignoreNextPop = false; }
    }
}

export function topLayer(): number | null {
    return stack.length ? stack[stack.length - 1].id : null;
}

export function hasLayers(): boolean {
    return stack.length > 0;
}

window.addEventListener('popstate', () => {
    if (ignoreNextPop) { ignoreNextPop = false; return; }
    const current = stateLayerId();
    // Close every layer opened after the entry we are now on.
    while (stack.length && stack[stack.length - 1].id !== current) {
        const top = stack[stack.length - 1];
        closingFromHistory = top.id;
        try { top.close(); } finally { closingFromHistory = null; }
        // close() normally pops itself; guard against a close() that forgot.
        if (stack.length && stack[stack.length - 1].id === top.id) stack.pop();
    }
});

// Capture phase so this runs before the per-page keydown handlers that still exist.
document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape' || stack.length === 0) return;
    e.stopImmediatePropagation();
    e.preventDefault();
    stack[stack.length - 1].close();
}, true);
